import { v4 as uuid } from 'uuid';
import { z } from 'zod';
import type { StateToken } from '@ngxs/store';
import { BaseSchema } from '../base/base.entity.model';
import type { Base } from '../base/base.entity.model';
import * as FlatModels from '../../internal';

export const BlockUiType = z.enum([
  'none',
  'saving',
  'undo',
  'redo',
  'loading',
  'uploading'
]);
export type BlockUiType = z.infer<typeof BlockUiType>;

export const EntityQueryType = z.enum([
  'ANNOTATION_ENTITY',
  'ANNOTATION_GROUP_ENTITY',
  'ANNOTATION_LABEL_ENTITY',
  'ANNOTATION_SOURCE_ENTITY',
  'ANNOTATION_TARGET_ENTITY',
  'CHOICE_ENTITY',
  'COPYRIGHT_ENTITY',
  'FREE_FORM_STYLE_ENTITY',
  'IMAGE_ENTITY',
  'KEYMAP_COLUMN_ENTITY',
  'KEYMAP_LABEL_ENTITY',
  'KEYMAP_STYLE_ENTITY',
  'LAYER_ENTITY',
  'MEDIA_ENTITY',
  'METADATA_ENTITY',
  'MODALITY_ENTITY',
  'NODE_ENTITY',
  'ROOT_NODE_ENTITY',
  'PAGE_ENTITY',
  'POINT_OF_INTEREST_ENTITY',
  'QUESTION_ENTITY',
  'STAIN_ENTITY',
  'USER_ENTITY',
  'VOCABULARY_GROUP_ENTITY',
  'VOCABULARY_WORD_ENTITY',
  // Special
  'DETACHED_PAGE',
  'SLIDE_VIEWER_PAGE',
  'TRAINING_VIEWER_SLIDE',
  'TRAINING_VIEWER_BLOCK',
  'FIND_SLIDES',
  'FIND_MARKERS',
  'FIND_MEDIAS',
  'TEXT_EXPORT',
  'MEDIA_USED_IN'
]);
export type EntityQueryType = z.infer<typeof EntityQueryType>;

export const HistorySnapshotItemModifierType = z.enum([
  'ADD',
  'UPDATE',
  'REMOVE',
]);
export type HistorySnapshotItemModifierType = z.infer<typeof HistorySnapshotItemModifierType>;

export const EntityQueryResultSchema = z.object({
  stateToken: EntityQueryType,
  entity: BaseSchema,
  count: z.number(),
});

export type EntityQueryResult = z.infer<typeof EntityQueryResultSchema>;

export const EntityQueryGetByIdSchema = z.object({
  type: EntityQueryType,
  id: z.string().uuid()
});

export type EntityQueryGetById = z.infer<typeof EntityQueryGetByIdSchema>;

export const EntityQueryGetByIdsSchema = z.object({
  type: EntityQueryType,
  ids: z.array(z.string().uuid())
});

export type EntityQueryGetByIds = z.infer<typeof EntityQueryGetByIdsSchema>;

export const EntityMappingItemSchema = z.object({
  getEntitySuccess: z.any(),
  getEntitiesSuccess: z.any(),
  modifyEntity: z.boolean(),
  modifyEntitySuccess: z.any(),
  modifyEntitiesSuccess: z.any(),
  undoSnapshot: z.any().nullable(),
  redoSnapshot: z.any().nullable(),
  cleanUpSnapshots: z.any().nullable(),
});

export const EntityMappingSchema = z.record(z.string(), EntityMappingItemSchema);

export type EntityMapping = z.infer<typeof EntityMappingSchema>;

export class HistorySnapshotItem {
  readonly id: string;

  constructor(public readonly stateToken: StateToken<FlatModels.EntityStatesModels.EntityStateModel<Base | FlatModels.UserEntityModels.User>>,
              public readonly modifierType: HistorySnapshotItemModifierType,
              public readonly entityId: string,
              public readonly previousSnapshotId: string | null,
              public readonly followingSnapshotId: string | null,
              public readonly user: FlatModels.UserEntityModels.User | null) {
    this.id = uuid();
  }
}

export const HistorySnapshotSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  url: z.string(), // or name it scope
  fromUrl: z.string(),
  saved: z.boolean(),
  snapshotItems: z.array(z.instanceof(HistorySnapshotItem)),
})

export type HistorySnapshot = z.infer<typeof HistorySnapshotSchema>;

export const SaveSnapshotItemSchema = z.object({
  id: z.string().nullable().default(null),
  snapshotId: z.string().nullable().default(null),
  stateToken: EntityQueryType,
  modifierType: HistorySnapshotItemModifierType,
  entity: z.any(),
});

export type SaveSnapshotItem = z.infer<typeof SaveSnapshotItemSchema>;

export const GetByIdSchema = z.object({
  id: z.string().uuid(),
});

export type GetById = z.infer<typeof GetByIdSchema>;

export const GetByIdsSchema = z.object({
  ids: z.array(z.string().uuid()),
});

export type GetByIds = z.infer<typeof GetByIdsSchema>;
